/**
 * A patient's lens history, shown while a prescription is written: their earlier prescriptions of the
 * kind being written, and what they wear now of that kind - from their latest collected dispensing job
 * - so the prescriber can compare the new Rx with what came before. The other kind gets one line.
 *
 * Reads what the dashboard tiles have already loaded, and loads it when they haven't. Dispensing
 * jobs are only read for someone who may see them.
 */
app.component("lensRxHistory", {
	templateUrl: "app/src/js/components/lensPrescribing/lensRxHistory.html",
	controller: "lensRxHistoryController",
	bindings: {
		patientId: "<",
		// The prescription being written or shown, which is left out.
		excludeId: "<",
		// Only prescriptions issued before this are listed - all of them for a new prescription.
		before: "<",
		// The kind being written - "glasses" (the default) or "contacts". Earlier ones of that kind can be used.
		kind: "<",
		// Offered on each earlier prescription of the kind being written, when given: onUse({ prescription }).
		onUse: "&?",
		readOnly: "<",
	},
});

app.controller("lensRxHistoryController", [
	"$scope",
	"$q",
	"LensPrescribingStore",
	"LensDispensingStore",
	"Session",
	function ($scope, $q, Rx, Store, Session) {
		const $ctrl = this;
		$scope.R = Rx;
		$scope.S = Store;
		$scope.state = { loading: false };
		// The newest few earlier prescriptions show; the rest are a click away.
		$scope.limit = 3;

		const canSeeJobs = Session.hasPermission("CLINICAL_LENS_DISPENSE");

		$ctrl.$onInit = function () {
			const id = $ctrl.patientId;
			if (!id) return;
			const loads = [];
			if (!Rx.forPatient(id).length) loads.push(Rx.load(id).catch(angular.noop));
			if (canSeeJobs && !Store.getJobs(id).length) loads.push(Store.loadPatientJobs(id).catch(angular.noop));
			if (!loads.length) return;
			$scope.state.loading = true;
			$q.all(loads).finally(() => ($scope.state.loading = false));
		};

		// Only the kind being written is compared: a contact lens Rx is vertex-converted and often rounded
		// to a spherical equivalent, so beside a spectacle Rx it invites a false comparison.
		const kind = () => $ctrl.kind || "glasses";
		const kindOf = (job) => (Store.isContacts(job) ? "contacts" : "glasses");

		// Issued (and withdrawn) prescriptions of this kind other than this one, newest first.
		$scope.previous = function () {
			const before = $ctrl.before ? new Date($ctrl.before) : null;
			return Rx.forPatient($ctrl.patientId).filter(
				(p) => p.type === kind() && p.id !== $ctrl.excludeId && !Rx.isDraft(p) && (!before || new Date(p.issuedAt) < before)
			);
		};
		$scope.shown = () => $scope.previous().slice(0, $scope.limit);
		$scope.showAll = () => ($scope.limit = Infinity);
		$scope.hiddenCount = () => Math.max(0, $scope.previous().length - $scope.limit);

		$scope.status = (p) => Rx.statusOf($ctrl.patientId, p);

		// The latest collected job of a kind: the glasses or contact lenses the patient has now.
		function latestCollected(ofKind) {
			if (!canSeeJobs) return null;
			return Store.getJobs($ctrl.patientId)
				.filter((j) => j.status === "COLLECTED" && kindOf(j) === ofKind)
				.sort((a, b) => new Date((b.order && b.order.collectedAt) || b.created) - new Date((a.order && a.order.collectedAt) || a.created))[0] || null;
		}
		$scope.wearing = function () {
			const job = latestCollected(kind());
			return job ? [job] : [];
		};

		// The other kind in one line - worth knowing (a contact lens wearer may want glasses only as a
		// backup), never its powers. "" when there's nothing.
		$scope.otherKind = function () {
			const other = kind() === "glasses" ? "contacts" : "glasses";
			const rx = Rx.forPatient($ctrl.patientId).find((p) => p.type === other && Rx.isIssued(p));
			const job = latestCollected(other);
			const when = (d) => moment(d).format("D MMM YYYY");
			if (other === "contacts") {
				const lens = rx ? Rx.title(rx) : job ? Rx.title(job) : "";
				if (!lens) return "";
				return `Also wears contact lenses: ${lens}${rx ? `, last prescribed ${when(rx.issuedAt)}` : ""}`;
			}
			if (rx) return `Also has a spectacle prescription: ${Rx.useMeta(rx.rx.use).title.toLowerCase()}, last prescribed ${when(rx.issuedAt)}`;
			return job ? `Also has glasses: ${Rx.glassesKind(job).toLowerCase()}, collected ${when((job.order && job.order.collectedAt) || job.created)}` : "";
		};

		// The Rx a job was made to, in the same words as a prescription.
		$scope.jobSummary = (job) => (Store.isContacts(job) ? Rx.summary({ type: "contacts", cl: job.cl }) : Rx.summary({ type: "glasses", rx: job.rx }));
		// A withdrawn prescription says why.
		$scope.withdrawnNote = (p) => (Rx.isWithdrawn(p) && p.withdrawn ? `Withdrawn: ${p.withdrawn.reason}` : "");

		// A withdrawn prescription is shown, but never used again.
		$scope.canUse = (p) => !!$ctrl.onUse && !$ctrl.readOnly && Rx.isIssued(p) && p.type === ($ctrl.kind || "glasses");
		$scope.use = (p) => $ctrl.onUse({ prescription: p });

		$scope.isEmpty = () => !$scope.state.loading && !$scope.previous().length && !$scope.wearing().length && !$scope.otherKind();
	},
]);
